import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Avatar, Label11, usd, pct, shortDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { KIND_LABELS, kindOf } from "@/lib/workKinds";
import { money } from "@/pages/owner/ObjectivesMoney";
import { cn } from "@/lib/utils";
import { Hourglass } from "lucide-react";

// design_handoff_team_roles/screens/Objectives.dc.html, the objective page: Money (task budget, AI spent, agreed fees,
// total cost so far) and Hiring terms (each task's terms with its history). Backend: objective_money.for_objective.
// Agreed fees are never deleted; Crewbench records them and never pays them.

function Cell({ label, children, strong }) {
  return (
    <div className="flex-[1_1_100px] bg-card px-3 py-2 flex flex-col">
      <Label11>{label}</Label11><span className={cn("text-[15px] leading-[22px]", strong && "font-semibold")}>{children}</span>
    </div>
  );
}

export function MoneyCard({ m }) {
  const agreed = m.terms.filter((t) => t.status === "agreed").length;
  const used = pct(m.spent_usd, m.budget_usd);
  return (
    <section aria-label="Money" className="bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-3.5" data-testid="objective-money">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">Money</h2>
        <span className="text-sm text-muted-foreground">US dollars</span>
      </div>
      <div className="flex flex-wrap gap-px bg-gr-hairline border border-gr-hairline rounded-lg overflow-hidden tabular-nums">
        <Cell label="Task budget">{m.budget_usd ? money(m.budget_usd) : "None set"}</Cell>
        <Cell label="AI spent">{usd(m.spent_usd)}</Cell>
        <Cell label={agreed > 1 ? "Agreed fees" : "Agreed fee"}>{m.fees_usd ? money(m.fees_usd) : "—"}</Cell>
        <Cell label="Total cost" strong>{usd(m.total_usd)}</Cell>
      </div>
      {m.budget_usd > 0 ? (
        <div className="flex flex-col gap-1.5 text-sm tabular-nums">
          <div className="flex justify-between gap-3"><span className="text-muted-foreground">Task budget used</span><span>{usd(m.spent_usd)} of {money(m.budget_usd)} · {used}%</span></div>
          <div className="relative h-1 rounded-sm bg-gr-surface-2">
            <div className={cn("h-full rounded-sm", m.spent_usd >= m.budget_usd ? "bg-gr-danger" : "bg-foreground")} style={{ width: `${Math.min(100, used)}%` }} />
            <span className="absolute -top-0.5 start-1/2 w-px h-2 bg-muted-foreground" aria-hidden="true" />
            <span className="absolute -top-0.5 start-[80%] w-px h-2 bg-muted-foreground" aria-hidden="true" />
          </div>
          <span className="text-muted-foreground">Sessions stop when the task budget is used up. Alerts at 50%, 80% and 100%.</span>
        </div>
      ) : <span className="text-sm text-muted-foreground">No task budget set: nothing stops its sessions but your other budgets.</span>}
    </section>
  );
}

// one line of a task's history, as the founder reads it
function said(h, t) {
  const by = h.by_name || "Someone";
  switch (h.event) {
    case "offered":
      return t.engagement === "independent" ? `${by} offered ${money(h.fee_usd)} for this task, with a ${money(h.budget_usd)} task budget.`
        : `${by} assigned this task, with a ${money(h.budget_usd)} task budget.`;
    case "agreed": return `${by} agreed.`;
    case "assigned": return `${by} joined and has the task.`;
    case "fee_change_offered": return `Fee change to ${money(h.fee_usd)}: "${h.reason}"`;
    case "fee_change_agreed": return `${by} agreed to ${money(h.fee_usd)}.`;
    case "fee_change_withdrawn": return `${by} withdrew the change to ${money(h.fee_usd)}.`;
    default: return `${by} withdrew the offer.`;
  }
}

const WaitingWord = () => (
  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.09em] text-gr-warning">
    <Hourglass className="w-3.5 h-3.5" aria-hidden="true" />Waiting for their agreement
  </span>
);

function Terms({ t, onChangeFee, onWithdraw, busy }) {
  const p = t.person;
  const first = (p.name || "").split(" ")[0];
  const independent = t.engagement === "independent";
  const line = p.title ? [p.title, KIND_LABELS[kindOf(p)]].join(" · ") + (p.removed ? " · Removed" : "") : "Invite sent";
  return (
    <div className="flex flex-col gap-3.5" data-testid={`terms-${t.id}`}>
      <div className="flex items-center gap-3">
        <Avatar name={p.name} size={28} />
        <span className="flex-1 min-w-0 flex flex-col"><span className="font-semibold">{p.name}</span><span className="text-sm text-muted-foreground">{line}</span></span>
        <span className="text-end flex flex-col tabular-nums">
          <span className="font-semibold">{independent ? `${money(t.fee_usd)}, fixed` : "No fee"}</span>
          {t.status === "offered" ? <WaitingWord />
            : <span className="text-sm text-muted-foreground">{t.agreed_at ? `Agreed ${shortDate(t.agreed_at, true)}` : `Assigned ${shortDate(t.offered_at)}`}</span>}
        </span>
      </div>
      <div className="flex flex-col text-sm">
        <span className="pb-1"><Label11>History</Label11></span>
        {t.history.map((h, i) => (
          <div key={i} className="grid grid-cols-[56px_minmax(0,1fr)] gap-3 py-[7px] border-t">
            <span className="text-muted-foreground tabular-nums">{shortDate(h.at)}</span>
            <span className="flex flex-col">
              <span>{said(h, t)}</span>
              {h.event === "fee_change_offered" && t.pending?.at === h.at && <WaitingWord />}
            </span>
          </div>
        ))}
      </div>
      {independent && (
        <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
          {t.pending ? `The agreed ${money(t.fee_usd)} stands until ${first} agrees to the change. `
            : t.status === "offered" ? `${first} isn't assigned until they agree. ` : ""}
          Crewbench doesn't pay fees; this records what you agreed.
        </p>
      )}
      {independent && !p.removed && (
        <div className="flex flex-wrap gap-2">
          {t.status === "agreed" && !t.pending && <Button variant="outline" onClick={() => onChangeFee(t)} data-testid={`terms-fee-${t.id}`}>Change the fee</Button>}
          {(t.status === "offered" || t.pending) && (
            <Button variant="outline" disabled={busy} onClick={() => onWithdraw(t)} data-testid={`terms-withdraw-${t.id}`}>
              {t.pending ? "Withdraw the change" : "Withdraw the offer"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function TermsCard({ m, onChangeFee }) {
  const qc = useQueryClient();
  const withdraw = useMutation({
    mutationFn: async (t) => (await api.post(`/assignments/${t.id}/withdraw`)).data,
    onSuccess: (d) => {
      ["objective", "objectives", "team"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(d.withdrawn === "task" ? "Offer withdrawn. We told them." : "Fee change withdrawn. The agreed fee stays.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <section aria-label="Hiring terms" className="bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-3.5" data-testid="objective-terms">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">Hiring terms</h2>
        <span className="text-sm text-muted-foreground">Agreed fees can't be deleted</span>
      </div>
      {m.terms.length ? m.terms.map((t, i) => (
        <div key={t.id} className={cn(i > 0 && "pt-3.5 border-t")}>
          <Terms t={t} onChangeFee={onChangeFee} onWithdraw={(x) => withdraw.mutate(x)} busy={withdraw.isPending} />
        </div>
      )) : <span className="text-sm text-muted-foreground [text-wrap:pretty]">No hiring terms yet. Hire someone for this to agree a fixed fee and a task budget with them.</span>}
    </section>
  );
}
